import { IPagedDataResults } from '@dealpoint/deprecated-lib/src/Models'
import { ITerm } from '@dealpoint/types'
import { PayloadAction, createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit'
import { omit } from 'lodash'
import { batch } from 'react-redux'

import { APIClient } from '../APIClient'
import { ITermTypeSearchQuery, PayloadActionWithId } from '../types'

// eslint-disable-next-line import/no-cycle
import { RootState } from './types'

interface TermSearchState {
  data: ITerm[]
  error: boolean
  loading: boolean
  meta: Omit<IPagedDataResults, 'records'> & { hasMore: boolean }
  query: ITermTypeSearchQuery
}

const initialState: Record<string, TermSearchState> = {}

const slice = createSlice({
  name: 'termSearch',
  initialState,
  reducers: {
    remove(state, action: PayloadAction<string>) {
      state = omit(state, action.payload)
    },
    setError(state, action: PayloadActionWithId<{ error: boolean }>) {
      const { id, error } = action.payload
      state[id].error = error
    },
    setLoading(state, action: PayloadActionWithId<{ loading: boolean }>) {
      const { id, loading } = action.payload
      state[id].loading = loading
    },
    setQuery(state, action: PayloadActionWithId<{ query: ITermTypeSearchQuery }>) {
      const { id, query } = action.payload
      state[id] = { ...state[id], query }
    },
    setQuerySearchTerm(state, action: PayloadActionWithId<{ value: string }>) {
      const { id, value } = action.payload
      state[id].query.query = value
    },
    setResult(state, action: PayloadActionWithId<{ result: IPagedDataResults<ITerm> }>) {
      const {
        id,
        result: { records, ...meta },
      } = action.payload
      state[id].data = meta.pageNumber > 1 ? state[id].data.concat(records) : records
      state[id].meta = {
        ...meta,
        hasMore: records.length && meta.pageNumber < meta.totalPages,
      }
    },
  },
})

// Actions
const { setError, setLoading, setQuery, setResult } = slice.actions

export const { setQuerySearchTerm } = slice.actions

interface InitializeProps extends Pick<ITermTypeSearchQuery, 'pageSize' | 'sort'> {
  id: string
}

export const initialize = createAsyncThunk(
  'termSearch/init',
  ({ id, pageSize, sort }: InitializeProps, { dispatch, getState }) => {
    const searches = selectTermSearch(getState() as RootState)
    if (searches[id]) {
      return
    }
    dispatch(search({ id, query: { pageSize, sort } }))
  }
)

interface SearchProps {
  id: string
  query: Partial<ITermTypeSearchQuery>
}

export const search = createAsyncThunk(
  'termSearch/search',
  async ({ id, query: partialQuery }: SearchProps, { dispatch, getState }) => {
    const searches = selectTermSearch(getState() as RootState)
    const query = searches[id]?.query ?? {}
    const queryParams = { ...query, ...partialQuery }

    batch(() => {
      dispatch(setQuery({ id, query: queryParams }))
      dispatch(setLoading({ id, loading: true }))
    })

    try {
      const result = await APIClient.makeAPICall('GET', '/search/terms', {
        queryParams,
        timeoutInMS: 50000,
      })
      dispatch(setResult({ id, result }))
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error)
      dispatch(setError({ id, error: true }))
    } finally {
      dispatch(setLoading({ id, loading: false }))
    }
  }
)

// Selectors
const selectTermSearch = (state: RootState) => state.termTypesSearch

export const makeSelectTermSearch = (id: string) =>
  createSelector(selectTermSearch, (searches) => searches[id] ?? ({} as TermSearchState))

// Reduce
export default slice.reducer
